import React from "react";
import { useState, useEffect } from "react";
import { Container, Box, Button, Typography, Grid2 } from "@mui/material";
import { Link } from "react-router-dom";
import PropertyCard from "../../property-card/card";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import HomeIcon from "@mui/icons-material/Home";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../firestore-DB-config/firebase-config";

interface PropertyCardProps {
  id: string;
  image: string;
  price: string;
  title: string;
  address: string;
  beds: number;
  baths: number;
  label: string;
  labelColor: "primary" | "error" | "success"; // Union type
  labelIcon: JSX.Element;
  type: string;
}

const LatestListedProperties: React.FC = () => {
  const [properties, setProperties] = useState<PropertyCardProps[]>([]);

  // Function to map labels to icons
  const getLabelIcon = (label: string): JSX.Element => {
    switch (label) {
      case "Newest":
        return <HomeIcon />;
      case "Discounted":
        return <AttachMoneyIcon />;
      case "Popular":
        return <WhatshotIcon />;
      default:
        return <HomeIcon />; // Default to HomeIcon if no match
    }
  };

  // Fetch properties data from Firestore using promises
  useEffect(() => {
    getDocs(collection(db, "properties"))
      .then((querySnapshot) => {
        const propertiesData = querySnapshot.docs.map((doc) => {
          const data = doc.data(); // Fetch the document's data

          // Ensure that the data has all the required fields and matches PropertyCardProps
          return {
            id: doc.id, // Firestore document ID
            image: data.imageUrls || "", // Provide default values if necessary
            price: data.price || "",
            title: data.title || "",
            address: data.address || "",
            beds: data.beds || 0,
            baths: data.baths || 0,
            label: data.label || "",
            labelColor: data.labelColor || "primary",
            labelIcon: getLabelIcon(data.label || ""), // Dynamically set labelIcon
          } as PropertyCardProps; // Ensure the structure matches the PropertyCardProps interface
        });

        setProperties(propertiesData);
      })
      .catch((error) => {
        console.error("Error fetching properties: ", error);
      });
  }, []);

  return (
    <Container sx={{ my: { xs: 6, md: 8 }, px: { xs: 2, md: 4, lg: 0 } }}>
      {/* Header Section */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={5}
        flexDirection={{ xs: "column", md: "row" }}
      >
        <Box textAlign={{ xs: "center", md: "left" }}>
          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{
              fontSize: { xs: "24px", md: "32px" },
              color: "rgba(1, 29, 51, 1)",
            }}
          >
            Latest Listed Properties
          </Typography>
          <Typography
            color="textSecondary"
            marginTop={1}
            sx={{
              fontSize: { xs: "14px", sm: "16px" },
              mt: { xs: 1, md: 1 },
            }}
          >
            Donec Porttitor Euismod Dignissim. Nullam A Lacina Ipsum, Nec
            Dignissim Purus. Nulla Convallis Ipsum Molestie.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          sx={{
            borderRadius: 4,
            mt: { xs: 2, md: 0 },
            px: "26px",
            whiteSpace: "nowrap",
            textTransform: "none", 
            transition: "background-color 0.3s, transform 0.3s",
            "&:hover": { transform: "scale(1.1)" },
          }}
          component={Link}
          to="/view-all"
          onClick={() => window.scrollTo(0, 0)}
        >
          View All
        </Button>
      </Box>
      {/* Property Cards Row */}
      <Grid2
        container
        spacing={{ xs: 4, sm: 8, md: 10 }}
        justifyContent={{ xs: "center", md: "left" }}
      >
        {properties.slice(0, 3).map((property) => (
          <Grid2 key={property.id} size={{ xs: 9.5, md: 6, lg: 4 }}>
            <PropertyCard prp={property} />
          </Grid2>
        ))}
      </Grid2>
    </Container>
  );
};

export default LatestListedProperties;
